import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyProducts, useProductCategories, useIssueCategories } from '@/hooks/useProducts';
import { useCreateTicket } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { toast } from 'sonner';
import { ArrowLeft, Check, Compass, MapPin } from '@phosphor-icons/react';
import useAppStore from '@/stores/appStore';

const STEPS = {
  SELECT_PRODUCT: 1,
  SELECT_ISSUE_AND_DESCRIBE: 2,
  FILL_ADDRESS_AND_GPS: 3,
  REVIEW: 4,
};

export const NewTicket = () => {
  const navigate = useNavigate();
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  const [step, setStep] = useState(STEPS.SELECT_PRODUCT);
  const [formData, setFormData] = useState({
    productId: '',
    issueCategoryIds: [],
    description: '',
    serviceAddress: '',
    pincode: '',
    streetAddress: '',
    locality: '',
    landmark: '',
    city: '',
    state: '',
    customerLat: null,
    customerLng: null,
  });
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [geocodeTimeoutId, setGeocodeTimeoutId] = useState(null);
  const [gpsPermissionDenied, setGpsPermissionDenied] = useState(false);
  
  // Pincode lookup states
  const [pincodeRecords, setPincodeRecords] = useState([]);
  const [fetchingPincode, setFetchingPincode] = useState(false);
  const [step3SubmitAttempted, setStep3SubmitAttempted] = useState(false);

  const { data: products, isLoading: productsLoading } = useMyProducts();
  const { data: categories } = useProductCategories();
  const selectedProduct = products?.find(p => p.id === formData.productId);
  const { data: issues } = useIssueCategories(selectedProduct?.category_id);
  const createTicket = useCreateTicket();

  const fetchPincodeData = async (pin) => {
    const cacheKey = `pincode_${pin}`;
    const cachedStr = localStorage.getItem(cacheKey);
    if (cachedStr) {
      try {
        const cached = JSON.parse(cachedStr);
        if (Date.now() - cached.timestamp < 24 * 60 * 60 * 1000) {
          return cached.records;
        }
      } catch (e) {
        console.warn('Failed to parse cached pincode data', e);
      }
    }

    const API_KEY = process.env.REACT_APP_GOV_PINCODE_API_KEY || "579b464db66ec23bdd000001f06cf0cb978841b27a139f73a62a9c8a";
    const url = `https://api.data.gov.in/resource/5c2f62fe-5afa-4119-a499-fec9d604d5bd?api-key=${API_KEY}&format=json&filters[pincode]=${pin}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error('Failed to fetch pincode details from Gov API');
      const data = await res.json();
      const records = data.records || [];
      
      if (records.length > 0) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            records,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('Failed to store pincode cache', e);
        }
      }
      return records;
    } catch (e) {
      clearTimeout(timeoutId);
      throw e;
    }
  };

  const triggerPincodeLookup = async (pin) => {
    setFetchingPincode(true);
    try {
      const records = await fetchPincodeData(pin);
      setPincodeRecords(records);
      if (records && records.length > 0) {
        const first = records[0];
        const toTitleCase = (str) => str ? str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.substring(1)).join(' ') : '';
        const cleanState = toTitleCase(first.statename);
        const cleanCity = toTitleCase(first.district);
        
        setFormData(prev => {
          const updated = {
            ...prev,
            state: cleanState,
            city: cleanCity,
            pincode: pin
          };
          
          if (first.latitude && first.latitude !== 'NA' && first.longitude && first.longitude !== 'NA') {
            updated.customerLat = parseFloat(first.latitude);
            updated.customerLng = parseFloat(first.longitude);
          }
          
          updated.serviceAddress = updateCombinedAddress(updated);
          return updated;
        });
        toast.success('Pincode details loaded successfully!');
      } else {
        toast.warning('Pincode details not found in government database.');
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.warn('Pincode lookup timed out');
      } else {
        console.error(e);
        toast.error('Could not load pincode details.');
      }
    } finally {
      setFetchingPincode(false);
    }
  };

  const updateCombinedAddress = (fields) => {
    const { streetAddress, locality, landmark, city, state, pincode } = fields;
    const parts = [
      streetAddress?.trim(),
      locality?.trim(),
      landmark?.trim() ? `Landmark: ${landmark.trim()}` : null,
      city?.trim(),
      state?.trim(),
      pincode?.trim()
    ].filter(Boolean);
    return parts.join(', ');
  };

  const geocodeAddress = async (updatedFields) => {
    const { streetAddress, city, state, pincode } = updatedFields;
    if (!city?.trim() || !state?.trim() || !pincode?.trim()) {
      return;
    }

    const queryParts = [streetAddress?.trim(), city.trim(), state.trim(), pincode.trim()].filter(Boolean);
    const query = queryParts.join(', ');

    setGeocodingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
        {
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'KwiccApp/1.0'
          }
        }
      );
      if (!response.ok) throw new Error('OSM Geocoding failed');
      const data = await response.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        setFormData(prev => ({
          ...prev,
          customerLat: lat,
          customerLng: lng
        }));
      }
    } catch (error) {
      console.error('Failed to geocode address:', error);
    } finally {
      setGeocodingAddress(false);
    }
  };

  const handleAddressFieldChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      updated.serviceAddress = updateCombinedAddress(updated);
      
      if (field === 'pincode' && value.length === 6 && /^\d+$/.test(value)) {
        triggerPincodeLookup(value);
      }
      
      if (geocodeTimeoutId) {
        clearTimeout(geocodeTimeoutId);
      }
      const newTimeoutId = setTimeout(() => {
        geocodeAddress(updated);
      }, 1000);
      setGeocodeTimeoutId(newTimeoutId);
      
      return updated;
    });
  };

  const handleIssueToggle = (issueId) => {
    setFormData(prev => {
      const ids = prev.issueCategoryIds.includes(issueId)
        ? prev.issueCategoryIds.filter(id => id !== issueId)
        : [...prev.issueCategoryIds, issueId];
      return { ...prev, issueCategoryIds: ids };
    });
  };

  useEffect(() => {
    return () => {
      if (geocodeTimeoutId) clearTimeout(geocodeTimeoutId);
    };
  }, [geocodeTimeoutId]);

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }

    setDetectingLocation(true);
    const toastId = toast.loading('Detecting location...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setGpsPermissionDenied(false);
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setFormData(prev => ({
          ...prev,
          customerLat: lat,
          customerLng: lng,
        }));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            {
              headers: {
                'Accept-Language': 'en',
                'User-Agent': 'KwiccApp/1.0'
              },
              signal: controller.signal
            }
          );
          clearTimeout(timeoutId);
          if (!response.ok) throw new Error('OSM Reverse Geocoding failed');
          
          const data = await response.json();
          const addressObj = data.address || {};
          
          const postcode = addressObj.postcode || '';
          const city = addressObj.city || addressObj.town || addressObj.village || addressObj.suburb || '';
          const state = addressObj.state || '';
          const street = [
            addressObj.house_number,
            addressObj.road,
            addressObj.neighbourhood
          ].filter(Boolean).join(', ') || addressObj.suburb || '';
          
          setFormData(prev => {
            const updated = {
              ...prev,
              customerLat: lat,
              customerLng: lng,
              pincode: postcode || prev.pincode,
              city: city || prev.city,
              state: state || prev.state,
              streetAddress: street || prev.streetAddress,
            };
            updated.serviceAddress = updateCombinedAddress(updated) || data.display_name;
            return updated;
          });
          
          if (postcode) {
            triggerPincodeLookup(postcode);
          }
          
          toast.success('Location detected and address form pre-filled!', { id: toastId });
        } catch (error) {
          console.error(error);
          setFormData(prev => {
            const updated = {
              ...prev,
              customerLat: lat,
              customerLng: lng,
            };
            updated.serviceAddress = updated.serviceAddress || `Latitude: ${lat}, Longitude: ${lng}`;
            return updated;
          });
          toast.success(`Coordinates mapped, but address details could not be resolved. Please type details manually.`, { id: toastId });
        } finally {
          setDetectingLocation(false);
        }
      },
      (error) => {
        console.error(error);
        let errorMsg = 'Failed to detect location';
        if (error.code === 3) {
          errorMsg = 'Location request timed out. Please enter your address details manually.';
        } else if (error.code === 1) {
          setGpsPermissionDenied(true);
          errorMsg = 'Location permission denied. Please enter your address details manually.';
        } else if (error.code === 2) {
          errorMsg = 'Location unavailable. Please enter your address details manually.';
        }

        // Fallback to pincode records coordinates if they exist
        if (pincodeRecords && pincodeRecords.length > 0) {
          const first = pincodeRecords[0];
          if (first.latitude && first.latitude !== 'NA' && first.longitude && first.longitude !== 'NA') {
            const fallbackLat = parseFloat(first.latitude);
            const fallbackLng = parseFloat(first.longitude);
            setFormData(prev => ({
              ...prev,
              customerLat: fallbackLat,
              customerLng: fallbackLng
            }));
            toast.info('Using coordinates from pincode directory as fallback.', { id: toastId });
            setDetectingLocation(false);
            return;
          }
        }

        toast.error(errorMsg, { id: toastId });
        setDetectingLocation(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleNext = () => {
    if (step === STEPS.SELECT_PRODUCT) {
      if (!formData.productId) {
        toast.error('Please select a product');
        return;
      }
      const prod = products?.find(p => p.id === formData.productId);
      if (prod && prod.warranty_status === 'Expired' && prod.allow_out_of_warranty_tickets === false) {
        toast.error('Raising service requests is disabled for this out-of-warranty product.');
        return;
      }
    }
    if (step === STEPS.SELECT_ISSUE_AND_DESCRIBE) {
      if (formData.issueCategoryIds.length === 0) {
        toast.error('Please select at least one issue category');
        return;
      }
    }
    if (step === STEPS.FILL_ADDRESS_AND_GPS) {
      setStep3SubmitAttempted(true);
      if (!formData.streetAddress?.trim() || !formData.city?.trim() || !formData.state?.trim() || !formData.pincode?.trim()) {
        toast.error('Street Address, City, State, and Pincode are mandatory');
        return;
      }
      setStep3SubmitAttempted(false);
    }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    try {
      const selectedIssueLabels = formData.issueCategoryIds.map(id => {
        const issue = issues?.find(i => i.id === id);
        return issue ? issue.label : '';
      }).filter(Boolean);

      const compiledDescription = `Selected Issues:\n${selectedIssueLabels.map(l => `• ${l}`).join('\n')}\n\nCustomer Description:\n${formData.description}`;

      await createTicket.mutateAsync({
        product_id: formData.productId,
        issue_category_id: formData.issueCategoryIds[0],
        description: compiledDescription,
        service_address: formData.serviceAddress,
        customer_lat: formData.customerLat,
        customer_lng: formData.customerLng,
      });
      toast.success('Ticket created successfully! You will receive a WhatsApp confirmation.');
      navigate('/customer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case STEPS.SELECT_PRODUCT:
        return (
          <div className="space-y-4">
            <div>
              <Label className="label-micro mb-4 block">Select Product</Label>
              {productsLoading ? (
                <LoadingSpinner />
              ) : products?.length > 0 ? (
                <RadioGroup 
                  value={formData.productId} 
                  onValueChange={(value) => {
                    const prod = products?.find(p => p.id === value);
                    if (prod && prod.warranty_status === 'Expired' && prod.allow_out_of_warranty_tickets === false) {
                      toast.error("This product's warranty is expired, and raising service requests is disabled for this model.");
                      return;
                    }
                    setFormData({ ...formData, productId: value });
                  }}
                  data-testid="product-selection"
                >
                  <div className="space-y-3">
                    {products.map((product) => {
                      const isWarrantyExpired = product.warranty_status === 'Expired';
                      const isLockedOut = isWarrantyExpired && product.allow_out_of_warranty_tickets === false;

                      return (
                        <Card 
                          key={product.id} 
                          className={`p-4 transition-all ${
                            formData.productId === product.id ? 'border-primary border-2' : ''
                          } ${isLockedOut ? 'opacity-65 bg-slate-50 border-slate-200 cursor-not-allowed' : 'hover:border-slate-350 cursor-pointer'}`}
                          onClick={(e) => {
                            if (isLockedOut) {
                              e.preventDefault();
                              toast.error("Raising tickets for this product is blocked because its warranty has expired.");
                            }
                          }}
                        >
                          <label className={`flex items-start gap-3 ${isLockedOut ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}>
                            <RadioGroupItem value={product.id} id={product.id} disabled={isLockedOut} />
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <p className="font-semibold">{product.product_name || product.model_name}</p>
                                {isLockedOut && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-red-650 px-2 py-0.5 rounded bg-red-50 border border-red-200">
                                    Service Blocked
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">Serial: {product.serial_number}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {product.category_name || 'General Product'} • Warranty: {product.warranty_status || 'Checking...'}
                              </p>
                              {isLockedOut && (
                                <p className="text-[11px] text-red-650 font-semibold mt-1">
                                  Warranty has expired and service requests are disabled for this model.
                                </p>
                              )}
                            </div>
                          </label>
                        </Card>
                      );
                    })}
                  </div>
                </RadioGroup>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">No products registered</p>
                  <Button onClick={() => navigate('/customer/products')} data-testid="register-product-btn">
                    Register a Product
                  </Button>
                </div>
              )}
            </div>
          </div>
        );

      case STEPS.SELECT_ISSUE_AND_DESCRIBE:
        return (
          <div className="space-y-6">
            <div>
              <Label className="label-micro mb-3 block">What's the Issue? (Select all that apply)</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="issue-selection">
                {issues?.map((issue) => (
                  <Card 
                    key={issue.id} 
                    className={`p-3 cursor-pointer transition-all border flex items-center ${
                      formData.issueCategoryIds.includes(issue.id) ? 'border-primary ring-2 ring-primary bg-primary/5' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => handleIssueToggle(issue.id)}
                  >
                    <div className="flex items-center gap-2.5 cursor-pointer w-full" onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        id={issue.id}
                        checked={formData.issueCategoryIds.includes(issue.id)}
                        onCheckedChange={() => handleIssueToggle(issue.id)}
                      />
                      <label htmlFor={issue.id} className="font-semibold text-sm text-slate-850 cursor-pointer flex-1 select-none">
                        {issue.label}
                      </label>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="border-t pt-5">
              <Label htmlFor="description" className="label-micro mb-3 block">
                Describe the Issue (Optional)
              </Label>
              <Textarea
                id="description"
                placeholder="Please provide detailed information about the issue you're experiencing..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={6}
                className="resize-none focus-ring border-slate-300 text-slate-855"
                data-testid="description-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1.5 text-right font-medium">
                {formData.description.length} characters
              </p>
            </div>
          </div>
        );

      case STEPS.FILL_ADDRESS_AND_GPS:
        return (
          <div className="space-y-6 animate-fade-in">
            {gpsPermissionDenied && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 space-y-2 mb-4" data-testid="gps-denied-help">
                <p className="font-bold flex items-center gap-1.5">
                  <span>⚠️</span> Geolocation Permission Blocked
                </p>
                <p className="leading-relaxed font-medium">
                  Your browser is currently blocked from accessing your location. To proceed with auto-detection:
                </p>
                <ol className="list-decimal pl-5 space-y-1.5 font-medium">
                  <li>Click the <strong>lock/settings icon</strong> 🔒 (or site settings menu) next to the URL in your browser's address bar.</li>
                  <li>Find the <strong>Location</strong> permission and change it from Block/Ask to <strong>Allow</strong>.</li>
                  <li><strong>Reload/refresh</strong> this page and click "Detect GPS Location" again.</li>
                </ol>
              </div>
            )}

            {/* Address Form Section */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-slate-850 border-b pb-2">Service Address Details</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pincode */}
                <div className="space-y-1.5">
                  <Label htmlFor="pincode" className="text-xs font-bold uppercase tracking-wider text-slate-700 flex justify-between items-center">
                    <span>Pincode / Postal Code *</span>
                    {formData.customerLat && formData.customerLng && (
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 rounded flex items-center gap-0.5">
                        ● GPS Mapped
                      </span>
                    )}
                  </Label>
                  <div className="relative flex items-center">
                    <Input
                      id="pincode"
                      placeholder="e.g. 504273"
                      value={formData.pincode}
                      onChange={(e) => handleAddressFieldChange('pincode', e.target.value)}
                      className={`h-11 border-slate-300 font-mono text-slate-900 pr-20 ${step3SubmitAttempted && !formData.pincode?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      maxLength={6}
                      required
                    />
                    <div className="absolute right-1.5 flex items-center gap-1">
                      {fetchingPincode && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-1.5" />
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={detectLocation}
                        disabled={detectingLocation}
                        className="h-8 px-2.5 text-xs font-bold text-primary hover:text-primary-dark hover:bg-slate-100 flex items-center gap-1"
                      >
                        <Compass size={14} className={detectingLocation ? "animate-spin" : ""} />
                        {detectingLocation ? '...' : 'Auto'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium">Entering 6 digits will auto-fill State & City from Gov database</p>
                </div>

                {/* Locality Dropdown / Input */}
                <div className="space-y-1.5">
                  <Label htmlFor="locality" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Locality / Post Office
                  </Label>
                  {pincodeRecords && pincodeRecords.length > 0 ? (
                    <Select 
                      value={formData.locality} 
                      onValueChange={(val) => handleAddressFieldChange('locality', val)}
                    >
                      <SelectTrigger className="h-11 border-slate-350 bg-white text-slate-900 text-sm">
                        <SelectValue placeholder="Select post office / locality" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border">
                        {pincodeRecords.map((rec) => (
                          <SelectItem key={rec.officename} value={rec.officename}>
                            {rec.officename}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="locality"
                      placeholder="e.g. Kothimir"
                      value={formData.locality}
                      onChange={(e) => handleAddressFieldChange('locality', e.target.value)}
                      className="h-11 border-slate-300 text-slate-900"
                    />
                  )}
                </div>

                {/* Street Address */}
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="streetAddress" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Street Address / House No. / Apartment *
                  </Label>
                  <Input
                    id="streetAddress"
                    placeholder="e.g. Flat 402, Building A, Main Road"
                    value={formData.streetAddress}
                    onChange={(e) => handleAddressFieldChange('streetAddress', e.target.value)}
                    className={`h-11 border-slate-300 text-slate-900 ${step3SubmitAttempted && !formData.streetAddress?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    required
                  />
                </div>

                {/* Landmark */}
                <div className="space-y-1.5">
                  <Label htmlFor="landmark" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Landmark (Optional)
                  </Label>
                  <Input
                    id="landmark"
                    placeholder="e.g. Near City Mall"
                    value={formData.landmark}
                    onChange={(e) => handleAddressFieldChange('landmark', e.target.value)}
                    className="h-11 border-slate-300 text-slate-900"
                  />
                </div>

                {/* City */}
                <div className="space-y-1.5">
                  <Label htmlFor="city" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    City / District *
                  </Label>
                  <Input
                    id="city"
                    placeholder="e.g. Mumbai"
                    value={formData.city}
                    onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                    className={`h-11 border-slate-300 text-slate-900 ${step3SubmitAttempted && !formData.city?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    required
                  />
                </div>

                {/* State */}
                <div className="space-y-1.5">
                  <Label htmlFor="state" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    State *
                  </Label>
                  <Input
                    id="state"
                    placeholder="e.g. Maharashtra"
                    value={formData.state}
                    onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                    className={`h-11 border-slate-300 text-slate-900 ${step3SubmitAttempted && !formData.state?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    required
                  />
                </div>
              </div>

              {/* Full Address Preview */}
              <div className="pt-4 border-t">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-2">
                  Full Compiled Address Preview
                </Label>
                <div className="p-3 bg-slate-50 border rounded-md text-sm text-slate-700 min-h-[44px]">
                  {formData.serviceAddress || <span className="text-slate-400 italic">Combined address will be shown here...</span>}
                </div>
              </div>
            </div>
          </div>
        );

      case STEPS.REVIEW:
        return (
          <div className="space-y-6">
            <div>
              <Label className="label-micro mb-2 block">Product</Label>
              <p className="font-semibold text-slate-800">{selectedProduct?.product_name || selectedProduct?.model_name}</p>
              <p className="text-xs text-muted-foreground">Serial: {selectedProduct?.serial_number}</p>
            </div>
            <div>
              <Label className="label-micro mb-2 block">Selected Issues</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {formData.issueCategoryIds.map(id => {
                  const issue = issues?.find(i => i.id === id);
                  return issue ? (
                    <span key={id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                      {issue.label}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
            <div>
              <Label className="label-micro mb-2 block">Service Address</Label>
              <p className="text-sm bg-slate-50 p-4 rounded-lg border border-slate-100 whitespace-pre-wrap">{formData.serviceAddress}</p>
              {formData.customerLat && formData.customerLng && (
                <p className="text-xs text-muted-foreground mt-1 font-medium">
                  GPS Coordinates: {formData.customerLat.toFixed(6)}, {formData.customerLng.toFixed(6)}
                </p>
              )}
            </div>
            <div>
              <Label className="label-micro mb-2 block">Description</Label>
              <p className="text-sm bg-slate-50 p-4 rounded-lg border border-slate-100 whitespace-pre-wrap">{formData.description}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout role="customer">
      <div className="p-6 lg:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <Button 
            variant="ghost" 
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/customer')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </Button>
          <h1 className="text-4xl font-black tracking-tight mb-2">Raise a Ticket</h1>
          <p className="text-muted-foreground">Step {step} of {Object.keys(STEPS).length}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full transition-all ${
                  s <= step ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <Card className="p-6 lg:p-8 shadow-md">
          {renderStepContent()}

          {/* Actions */}
          <div className="flex gap-4 mt-8">
            {step > 1 && (
              <Button 
                variant="outline" 
                onClick={() => { setStep3SubmitAttempted(false); setStep(step - 1); }}
                className="flex-1 font-bold h-11"
                data-testid="previous-btn"
              >
                Previous
              </Button>
            )}
            {step < STEPS.REVIEW ? (
              <Button 
                onClick={handleNext}
                className="flex-1 font-bold h-11"
                data-testid="next-btn"
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={createTicket.isPending}
                className="flex-1 font-bold h-11 text-white shadow"
                style={{ backgroundColor: brandColorAccent }}
                data-testid="submit-ticket-btn"
              >
                {createTicket.isPending ? 'Submitting...' : (
                  <>
                    <Check size={20} className="mr-2" />
                    Submit Ticket
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};